// Import all managers
import { apiManager } from "./managers/ApiManager.js";
import { videoManager } from "./managers/VideoManager.js";
import { hotspotManager } from "./managers/HotspotManager.js";
import { panelManager } from "./managers/PanelManager.js";
import { uiManager } from "./managers/UIManager.js";
import { eventManager } from "./managers/EventManager.js";
import { tooltipManager } from "./utils/tooltip.js";

/**
 * DynamicStepSystem - Main orchestrator class that coordinates all managers
 * to create a dynamic, interactive step-based experience
 */
export class DynamicStepSystem {
  constructor() {
    // Core system state
    this.config = null;
    this.currentStep = 1;
    this.steps = new Map();
    this.container = null;
    this.preloader = null;
    this.articles = null;
    this.files = null;

    // Manager references
    this.apiManager = apiManager;
    this.videoManager = videoManager;
    this.hotspotManager = hotspotManager;
    this.panelManager = panelManager;
    this.uiManager = uiManager;
    this.eventManager = eventManager;
    this.tooltipManager = tooltipManager;
  }

  /**
   * Initialize the entire system
   */
  async init() {
    try {
      // Initialize preloader
      this.initPreloader();
      this.updateProgress(10, "Initializing system...");

      // Load articles and files
      this.updateProgress(20, "Loading articles...");
      await this.loadArticles();

      // Load configuration
      this.updateProgress(40, "Loading configuration...");
      await this.loadConfiguration();

      // Generate steps
      this.updateProgress(60, "Generating steps...");
      this.generateSteps();

      // Initialize UI components
      this.updateProgress(80, "Initializing components...");
      this.initializeUI();

      // Setup event listeners
      this.updateProgress(90, "Setting up interactions...");
      this.setupEventListeners();

      // Load first step video immediately (critical for UX)
      this.updateProgress(95, "Loading initial content...");
      try {
        await this.videoManager.loadVideoOnDemand(
          1,
          this.steps.get(1).element,
          true,
          (progress, message) => {
            this.updateProgress(progress, message);
          },
        );
      } catch (error) {
        console.warn("Failed to load initial video, continuing anyway:", error);
      }

      // Start with first step
      this.updateProgress(100, "Ready!");
      await this.initializeFirstStep();

      // Hide preloader after a short delay
      setTimeout(() => {
        this.hidePreloader();
      }, 500);

      console.log("[DynamicStepSystem] initialization completed successfully");
    } catch (error) {
      console.error("[DynamicStepSystem] initialization failed:", error);
      this.updateProgress(100, "Error occurred during initialization");

      // Hide preloader even on error
      setTimeout(() => {
        this.hidePreloader();
      }, 2000);

      throw error;
    }
  }

  /**
   * Initialize preloader
   */
  initPreloader() {
    this.preloader = document.querySelector(".app-preloader");
    if (!this.preloader) {
      console.warn("[Preloader] element not found");
      return;
    }

    this.preloader.classList.remove("hidden");
    console.log("[Preloader] initialized");
  }

  /**
   * Update preloader progress
   */
  updateProgress(percentage, message) {
    if (!this.preloader) return;

    const progressFill = this.preloader.querySelector(".progress-fill");
    const progressText = this.preloader.querySelector(".progress-text");

    if (progressFill) {
      progressFill.style.width = `${percentage}%`;
    }

    if (progressText) {
      progressText.textContent = message;
    }

    console.log(`Progress: ${percentage}% - ${message}`);
  }

  /**
   * Hide preloader
   */
  hidePreloader() {
    if (this.preloader) {
      this.preloader.classList.add("hidden");
      console.log("[Preloader] hidden");

      setTimeout(() => {
        if (this.preloader && this.preloader.parentNode) {
          this.preloader.parentNode.removeChild(this.preloader);
          console.log("[Preloader] removed from DOM");
        }
      }, 500);
    }
  }

  /**
   * Load articles and files using ApiManager
   */
  async loadArticles() {
    try {
      this.articles = await this.apiManager.fetchArticles();
      this.files = await this.apiManager.fetchFiles();
      console.log("[Articles] and files loaded successfully");
    } catch (error) {
      console.error("Error loading articles/files:", error);
      throw error;
    }
  }

  /**
   * Load configuration using ApiManager
   */
  async loadConfiguration() {
    try {
      this.config = await this.apiManager.loadStepConfig();
      console.log("[Configuration] loaded successfully:", this.config);
    } catch (error) {
      console.error("Error loading configuration:", error);
      throw error;
    }
  }

  /**
   * Generate all steps from configuration
   */
  generateSteps() {
    this.container = document.querySelector(".stepper-container");
    if (!this.container) {
      throw new Error("Stepper container not found");
    }

    this.container.innerHTML = "";

    if (!this.config?.steps || !Array.isArray(this.config.steps)) {
      throw new Error("Invalid configuration: steps not found or not an array");
    }

    this.config.steps.forEach((stepConfig) => {
      const stepElement = this.createStepElement(stepConfig);
      this.container.appendChild(stepElement);
      this.steps.set(stepConfig.id, {
        config: stepConfig,
        element: stepElement,
      });
    });

    console.log(`Generated ${this.steps.size} steps`);
  }

  /**
   * Create a single step element
   */
  createStepElement(stepConfig) {
    console.log(`Creating step element: ${stepConfig.id}`);

    const stepDiv = document.createElement("div");
    stepDiv.className = `step step-${stepConfig.id}`;
    stepDiv.setAttribute("data-step", stepConfig.id);

    // Create background using VideoManager or direct creation
    const background = this.createBackground(stepConfig.background);
    stepDiv.appendChild(background);

    // Create hotspots using HotspotManager
    if (stepConfig.hotspots) {
      stepConfig.hotspots.forEach((hotspot) => {
        const hotspotElement = this.hotspotManager.createHotspot(
          hotspot,
          (hotspotElement) => this.handleHotspotClick(hotspotElement),
        );
        stepDiv.appendChild(hotspotElement);
      });

      // Create mobile hotspots with mobileHotspotsHeader as priority, fallback to panel title
      const mobileHeader = stepConfig.mobileHotspotsHeader || 
        stepConfig.mobileHeader || 
        (stepConfig.panel ? { title: stepConfig.panel.title } : null);

      const mobileHotspots = this.hotspotManager.createMobileHotspots(
        stepConfig.hotspots,
        mobileHeader,
        (hotspotElement) => this.handleHotspotClick(hotspotElement),
      );
      stepDiv.appendChild(mobileHotspots);
    }

    // Create panel using PanelManager
    if (stepConfig.panel) {
      const panel = this.panelManager.createPanel(
        stepConfig.id,
        stepConfig.panel,
        (targetStep) => this.goToStep(targetStep),
        (stepId, tabId) => this.updateHotspotVisibility(stepId, tabId),
        this.articles, // Pass loaded articles to PanelManager
      );
      stepDiv.appendChild(panel);
    }

    return stepDiv;
  }

  /**
   * Create background element
   */
  createBackground(bgConfig) {
    const container = document.createElement("div");
    container.className = "background-container";

    if (bgConfig.type === "image") {
      const img = document.createElement("img");
      img.src = bgConfig.src;
      img.alt = bgConfig.alt || "";
      img.className = "step-bg";
      container.appendChild(img);
    } else if (bgConfig.type === "video") {
      // Use VideoManager for video backgrounds
      return this.videoManager.createVideoBackground(bgConfig);
    } else if (bgConfig.type === "component") {
      const existingComponent = document.querySelector(
        `.${bgConfig.component}`,
      );
      if (existingComponent) {
        const componentClone = existingComponent.cloneNode(true);
        componentClone.className = `step-bg ${bgConfig.component}`;
        container.appendChild(componentClone);
      } else {
        console.warn(`Component ${bgConfig.component} not found in DOM`);
      }
    }

    return container;
  }

  /**
   * Initialize UI components
   */
  initializeUI() {
    // Initialize contact modal
    this.uiManager.initContactModal();

    // Initialize logo navigation
    this.uiManager.initLogoNavigation((targetStep) =>
      this.goToStep(targetStep),
    );
  }

  /**
   * Setup all event listeners
   */
  setupEventListeners() {
    this.eventManager.setupEventListeners({
      hotspotClickCallback: (hotspot) => this.handleHotspotClick(hotspot),
      nextStepCallback: () => this.goToNextStep(),
      prevStepCallback: () => this.goToPreviousStep(),
      navigationCallback: (targetStep) => this.goToStep(targetStep),
    });
  }

  /**
   * Navigate to a specific step with transition
   */
  async goToStep(stepNumber) {
    console.log(`🎬 Navigating to step ${stepNumber} with transition`);

    if (!this.steps.has(stepNumber)) {
      console.warn(`Step ${stepNumber} not found`);
      return;
    }

    const previousStep = this.currentStep;

    // Cleanup event listeners from previous step
    if (previousStep !== null) {
      this.eventManager.cleanupStepEvents(previousStep);
    }

    this.currentStep = stepNumber;

    // Load video for current step (lazy loading)
    try {
      await this.videoManager.loadVideoOnDemand(
        stepNumber,
        this.steps.get(stepNumber).element,
        false,
        (progress, message) => this.updateProgress(progress, message),
      );
    } catch (error) {
      console.warn(`Failed to load video for step ${stepNumber}:`, error);
    }

    // Get step elements
    const currentStepData = this.steps.get(stepNumber);
    const previousStepData = previousStep ? this.steps.get(previousStep) : null;

    // Perform transition
    await this.performCrossFadeTransition(previousStepData, currentStepData);

    // Update responsive UI
    this.uiManager.updateResponsiveUI(currentStepData.element);

    // Scroll to top on mobile devices for better UX (with small delay to ensure DOM is updated)
    if (this.uiManager.isMobile) {
      setTimeout(() => {
        this.uiManager.scrollToTop(true);
      }, 50);
    }

    // Handle step-specific logic
    this.handleStepTransition(currentStepData.config, previousStep);

    // Preload next step video in background
    this.videoManager
      .preloadNextStepVideo(stepNumber, this.steps)
      .catch((error) => {
        console.warn(`Failed to preload next step video:`, error);
      });

    console.log(`Successfully navigated to step ${stepNumber}`);
  }

  /**
   * Initialize first step without transition
   */
  async initializeFirstStep() {
    console.log(`Initializing first step without transition`);

    this.currentStep = 1;
    const firstStepData = this.steps.get(1);

    if (!firstStepData) {
      console.error("First step not found!");
      return;
    }

    this.steps.forEach((step) => {
      step.element.classList.remove(
        "active",
        "transitioning-out",
        "transitioning-in",
        "fade-in",
      );
      step.element.style.display = "none";
    });

    firstStepData.element.classList.add("active");
    firstStepData.element.style.display = "block";
    firstStepData.element.style.visibility = "visible";

    this.uiManager.updateResponsiveUI(firstStepData.element);

    // Scroll to top on mobile devices for better UX (with small delay to ensure DOM is updated)
    if (this.uiManager.isMobile) {
      setTimeout(() => {
        this.uiManager.scrollToTop(true);
      }, 50);
    }

    this.handleStepTransition(firstStepData.config, null);

    console.log(`First step initialized successfully`);
  }

  /**
   * Perform transition between steps
   */
  async performCrossFadeTransition(previousStepData, currentStepData) {
    return new Promise((resolve) => {
      console.log(`Starting cross-fade transition`);

      if (previousStepData) {
        previousStepData.element.classList.remove("active");
        previousStepData.element.classList.add("transitioning-out");
        previousStepData.element.style.display = "block";
      }

      currentStepData.element.classList.remove("active");
      currentStepData.element.classList.add("transitioning-in");
      currentStepData.element.style.display = "block";

      requestAnimationFrame(() => {
        currentStepData.element.classList.add("fade-in");

        setTimeout(() => {
          console.log(`🎬 Completing cross-fade transition`);

          if (previousStepData) {
            previousStepData.element.classList.remove("transitioning-out");
            previousStepData.element.style.display = "none";
          }

          currentStepData.element.classList.remove(
            "transitioning-in",
            "fade-in",
          );
          currentStepData.element.classList.add("active");
          currentStepData.element.style.display = "block";
          currentStepData.element.style.visibility = "visible";

          // Clean up all other steps
          this.steps.forEach((step, stepId) => {
            if (stepId !== this.currentStep) {
              step.element.classList.remove(
                "active",
                "transitioning-out",
                "transitioning-in",
                "fade-in",
              );
              step.element.style.display = "none";
            }
          });

          resolve();
        }, 800);
      });
    });
  }

  /**
   * Handle step transition logic with intelligent conflict prevention
   */
  handleStepTransition(stepConfig, previousStep) {
    console.log(
      `[Transition] Analyzing transitions for step ${this.currentStep}:`,
      stepConfig.transitions,
    );

    // Only handle transitions if they are configured
    if (!stepConfig.transitions?.next) {
      console.log(
        `[Transition] No transitions configured for step ${this.currentStep}`,
      );
      return;
    }

    const triggerType = stepConfig.transitions.trigger;
    const nextStep = stepConfig.transitions.next;
    const hasVideo = stepConfig.background?.type === "video";
    const hasDuration = stepConfig.duration;
    const hasHotspots = stepConfig.hotspots && stepConfig.hotspots.length > 0;

    // Detect potential conflicts
    const conflicts = [];
    if (triggerType === "video-end" && hasDuration) conflicts.push("video-end + duration");
    if (triggerType === "hotspot-click" && hasDuration) conflicts.push("hotspot-click + duration");
    if (triggerType === "duration" && hasHotspots) conflicts.push("duration + hotspots");
    if (!triggerType && hasDuration && hasHotspots) conflicts.push("auto-duration + hotspots");

    if (conflicts.length > 0) {
      console.warn(
        `[Transition] ⚠️ Potential conflicts detected for step ${this.currentStep}:`,
        conflicts.join(", ")
      );
    }

    // PRIORITY SYSTEM - Only ONE transition type will be set up:
    // 1. Explicit trigger type (highest priority)
    // 2. Video-end (if video exists)
    // 3. Duration fallback (lowest priority)

    let selectedTransition = null;

    // Priority 1: Explicit trigger type
    if (triggerType) {
      if (triggerType === "video-end" && hasVideo) {
        selectedTransition = "video-end";
      } else if (triggerType === "duration" && hasDuration) {
        selectedTransition = "duration";
      } else if (triggerType === "hotspot-click") {
        selectedTransition = "hotspot-click";
      } else if (triggerType === "back-button") {
        selectedTransition = "back-button";
      }
    }
    // Priority 2: Auto video-end (if no explicit trigger and video exists)
    else if (hasVideo) {
      selectedTransition = "video-end";
    }
    // Priority 3: Auto duration (if no explicit trigger, no video, but duration exists)
    else if (hasDuration) {
      selectedTransition = "duration";
    }

    console.log(
      `[Transition] Selected transition: ${selectedTransition} for step ${this.currentStep} → ${nextStep}`
    );

    // Set up ONLY the selected transition
    if (selectedTransition === "video-end") {
      const video = this.steps
        .get(this.currentStep)
        .element.querySelector("video");
      if (video) {
        console.log(
          `[Transition] ✅ Setting up video-end transition: Step ${this.currentStep} → Step ${nextStep}`,
        );

        // Start video playback
        video.play().catch((e) => console.log("Video autoplay failed:", e));

        this.eventManager.setupVideoEvents(
          video,
          () => {
            console.log(
              `[Transition] Video-end triggered: Step ${this.currentStep} → Step ${nextStep}`,
            );
            this.goToStep(nextStep);
          },
          this.currentStep,
        );
      }
    } else if (selectedTransition === "duration") {
      console.log(
        `[Transition] ✅ Setting up duration transition: Step ${this.currentStep} → Step ${nextStep} (${stepConfig.duration}ms)`,
      );
      this.eventManager.setupDurationTransition(
        this.currentStep,
        stepConfig.duration,
        () => {
          console.log(
            `[Transition] Duration triggered: Step ${this.currentStep} → Step ${nextStep}`,
          );
          this.goToStep(nextStep);
        },
      );
    } else if (selectedTransition === "hotspot-click" || selectedTransition === "back-button") {
      console.log(
        `[Transition] ✅ Manual transition configured: ${selectedTransition} (handled by event managers)`
      );
    }

    // Log what was IGNORED to prevent conflicts
    const ignored = [];
    if (selectedTransition !== "video-end" && hasVideo && triggerType !== "video-end") {
      ignored.push("video-end (video exists but not selected)");
    }
    if (selectedTransition !== "duration" && hasDuration) {
      ignored.push(`duration (${stepConfig.duration}ms available but not selected)`);
    }
    if (ignored.length > 0) {
      console.log(
        `[Transition] 🚫 Ignored transitions to prevent conflicts:`,
        ignored.join(", ")
      );
    }

    // Update hotspot visibility for panels with tabs
    if (stepConfig.panel?.tabs) {
      const activeTab = this.panelManager.getActiveTab(this.currentStep);
      this.updateHotspotVisibility(this.currentStep, activeTab);
    }

    console.log(`Step transition handled for step ${this.currentStep}`);
  }

  /**
   * Handle hotspot click
   */
  handleHotspotClick(hotspotElement) {
    const currentStepConfig = this.steps.get(this.currentStep).config;
    this.hotspotManager.handleHotspotClick(
      hotspotElement,
      (targetStep) => this.goToStep(targetStep),
      currentStepConfig,
    );
  }

  /**
   * Update hotspot visibility based on active tab
   */
  updateHotspotVisibility(stepId, activeTabId) {
    const stepData = this.steps.get(stepId);
    if (stepData) {
      this.hotspotManager.updateHotspotVisibility(
        stepData.element,
        activeTabId,
      );
    }
  }

  /**
   * Go to next step
   */
  goToNextStep() {
    const currentStepConfig = this.steps.get(this.currentStep).config;
    if (currentStepConfig.transitions?.next) {
      this.goToStep(currentStepConfig.transitions.next);
    }
  }

  /**
   * Go to previous step
   */
  goToPreviousStep() {
    const currentStepConfig = this.steps.get(this.currentStep).config;
    if (currentStepConfig.transitions?.prev) {
      this.goToStep(currentStepConfig.transitions.prev);
    }
  }

  /**
   * Get system statistics
   */
  getSystemStats() {
    return {
      currentStep: this.currentStep,
      totalSteps: this.steps.size,
      loadedArticles: this.articles?.length || 0,
      loadedFiles: this.files?.length || 0,
      videoStats: this.videoManager.getVideoStats(),
      hotspotStats: this.hotspotManager.getHotspotStats(),
      panelStats: this.panelManager.getPanelStats(),
      uiStats: this.uiManager.getUIStats(),
      eventStats: this.eventManager.getEventStats(),
    };
  }

  /**
   * Reset entire system
   */
  reset() {
    console.log("🔄 Resetting DynamicStepSystem...");

    // Reset all managers
    this.videoManager.reset();
    this.hotspotManager.reset();
    this.panelManager.reset();
    this.uiManager.reset();
    this.eventManager.reset();

    // Reset system state
    this.currentStep = 1;
    this.steps.clear();
    this.config = null;
    this.articles = null;
    this.files = null;

    // Clear container
    if (this.container) {
      this.container.innerHTML = "";
    }
  }
}

export const dynamicStepSystem = new DynamicStepSystem();
